/**
 * APPRAMEN — toppraden och spalten som varje skärm står i.
 *
 * Prototypens `.phone` är en attrapp på en webbsida; här ÄR appen telefonen.
 * Toppraden är densamma: bakåtknapp till vänster när skärmen har en förälder,
 * skärmens namn i mitten, initialer och kugghjul till höger.
 *
 * SKÄRMEN SKA INTE RITA TOPPRADEN. <Appram> gör det ur skärmkartan — titeln
 * och bakåtknappen kommer ur `Skarm.titel` och `Skarm.foralder` i
 * app/skarmar.ts. En skärm som ritar sin egen rubrik i toppraden gör kartan
 * till två ställen som kan säga emot varandra.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Ikon } from './Ikon';
import { initialer } from './Rader';
import { useNavigering } from '../app/navigering';

export interface Appramsprops {
  /** Den inloggades namn. Blir initialerna i cirkeln. */
  readonly namn: string;
  /** Kugghjulet. Utelämnad ritas ingen knapp. */
  readonly onInstallningar?: () => void;
  readonly children: ReactNode;
}

export const Appram = ({ namn, onInstallningar, children }: Appramsprops) => {
  const { plats, tillbaka } = useNavigering();
  const [rullad, sattRullad] = useState(false);
  const djup = plats.skarm.foralder !== null;

  // Hårfina linjen under toppraden dyker upp först när något rullat under den.
  useEffect(() => {
    const vidRullning = (): void => sattRullad(globalThis.scrollY > 4);
    vidRullning();
    globalThis.addEventListener('scroll', vidRullning, { passive: true });
    return () => globalThis.removeEventListener('scroll', vidRullning);
  }, []);

  return (
    <div className="k-app">
      <header className={rullad ? 'k-topprad k-rullad' : 'k-topprad'}>
        {djup && (
          <button type="button" className="backbtn" onClick={tillbaka}>
            <Ikon namn="tillbaka" storlek={20} tjocklek={2} />
            Tillbaka
          </button>
        )}
        <span className={djup ? 'wordmark k-djup' : 'wordmark'}>{plats.skarm.titel}</span>
        <span className="top-right">
          <span className="avatar" title={namn}>
            {initialer(namn)}
          </span>
          {onInstallningar !== undefined && (
            <button
              type="button"
              className="iconbtn"
              aria-label="Inställningar"
              onClick={onInstallningar}
            >
              <Ikon namn="installningar" storlek={19} />
            </button>
          )}
        </span>
      </header>
      <main className="k-kropp">{children}</main>
    </div>
  );
};

/**
 * Ramen för de skärmar som står UTANFÖR sessionen — inloggningen och
 * länksidan. Ingen topprad, ingen bakåtknapp; bara spalten och en logotyp.
 */
export const Yttreram = ({ children }: { readonly children: ReactNode }) => (
  <div className="k-app">
    <header className="k-topprad">
      <span className="wordmark">Klartid</span>
    </header>
    <main className="k-kropp">{children}</main>
  </div>
);
